import React from 'react';
import { useTranslation } from 'react-i18next';
import '../i18n';

const CHUNK_RETRY_KEY = 'dataunfold:chunk-load-retries';
const CHUNK_RETRY_WINDOW_MS = 5 * 60 * 1000;
const MAX_CHUNK_RETRIES = 2;

export const isChunkLoadError = (error) => (
  error?.name === 'ChunkLoadError'
  || /Loading (?:CSS )?chunk \S+ failed/i.test(String(error?.message || ''))
);

export const reserveChunkReload = (error, storage, now = Date.now()) => {
  if (!isChunkLoadError(error) || !storage) return 0;

  try {
    const previous = JSON.parse(storage.getItem(CHUNK_RETRY_KEY) || 'null');
    const withinRetryWindow = Number.isFinite(previous?.startedAt)
      && previous.startedAt <= now
      && now - previous.startedAt < CHUNK_RETRY_WINDOW_MS;
    const attempts = withinRetryWindow && Number.isInteger(previous.attempts)
      && previous.attempts >= 0 ? previous.attempts : 0;
    if (attempts >= MAX_CHUNK_RETRIES) return 0;

    const nextAttempt = attempts + 1;
    storage.setItem(CHUNK_RETRY_KEY, JSON.stringify({
      startedAt: withinRetryWindow ? previous.startedAt : now,
      attempts: nextAttempt,
    }));
    return nextAttempt;
  } catch (error) {
    // Restricted storage must never prevent the recovery screen from rendering.
    return 0;
  }
};

const RecoveryScreen = ({ isChunkError, isRetrying }) => {
  const { t } = useTranslation();

  return (
    <main style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24, background: '#f7f7ff' }}>
      <div style={{ maxWidth: 480, padding: 32, borderRadius: 16, background: '#fff', boxShadow: '0 8px 32px rgba(28, 28, 79, 0.12)', textAlign: 'center' }}>
        <h1 style={{ marginTop: 0, fontSize: 24, color: '#24244a' }}>
          {t('appRecovery.title', 'The interface could not load')}
        </h1>
        <p style={{ color: '#4b5563', lineHeight: 1.5 }}>
          {isRetrying
            ? t('appRecovery.retrying', 'A part of the interface could not be downloaded. Trying again…')
            : isChunkError
              ? t('appRecovery.chunkFailure', 'A part of the interface could not be downloaded. Please reload the page.')
              : t('appRecovery.renderFailure', 'An unexpected error interrupted the interface. Please reload the page.')}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ padding: '10px 20px', border: 0, borderRadius: 8, background: '#5757b0', color: '#fff', cursor: 'pointer' }}
        >
          {t('appRecovery.reload', 'Reload page')}
        </button>
      </div>
    </main>
  );
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, isRetrying: false };
    this.retryTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Application render failed:', error, info);
    let storage;
    try {
      storage = window.sessionStorage;
    } catch (storageError) {
      storage = null;
    }
    const attempt = reserveChunkReload(error, storage);
    if (attempt > 0) {
      this.setState({ isRetrying: true });
      this.retryTimer = window.setTimeout(() => window.location.reload(), attempt * 1500);
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) window.clearTimeout(this.retryTimer);
  }

  render() {
    const { error, isRetrying } = this.state;
    if (error) {
      return <RecoveryScreen isChunkError={isChunkLoadError(error)} isRetrying={isRetrying} />;
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
